/**
 * src/pages/WhereIsEverythingGoing/sections/SunLifecycleSection.tsx
 * "The Sun's long goodbye": interactive slider through the Sun's life (age 0 -> 12 billion years)
 * with live SVG visual representation (size, corona, core, colour changing across stages)
 * and an Earth panel describing habitability changes (brightening ~10%/Gyr, oceans lost 1-1.5 Gyr,
 * red giant engulfment probability Schröder & Smith 2008).
 * Labels: established for stellar evolution theory, research for detailed habitability modeling.
 */

import React, { useState } from "react";
import { SUN_LIFECYCLE_STAGES } from "../futureData";
import { EvidenceBadge } from "@/components/originBeyond/EvidenceBadge";
import { Footnote } from "@/components/originBeyond/Footnote";
import { SectionHeading } from "@/components/cosmic/SectionHeading";
import { Sun, Globe, AlertTriangle, Flame, Snowflake } from "lucide-react";
export function SunLifecycleSection() {
  const [sliderIndex, setSliderIndex] = useState(1); // Present day default (4.6 Gyr)
  const currentStage = SUN_LIFECYCLE_STAGES[sliderIndex] || SUN_LIFECYCLE_STAGES[1];

  // Visual parameters scaled from solar model data
  // Base SVG radius: 0.9 solar = 28px, 1.0 solar = 32px, 1.15 = 38px, 2.1 = 52px, 180 = 90px (clamped), 0.01 = 12px
  const getRenderRadius = (rSolar: number) => {
    if (rSolar < 0.1) return 12; // White dwarf core
    if (rSolar <= 1.0) return 26 + rSolar * 6;
    if (rSolar <= 2.5) return 32 + (rSolar - 1) * 16;
    return 88; // Red giant peak
  };
  const renderRadius = getRenderRadius(currentStage.radiusSolar);
  return <section id="sun-lifecycle" className="w-full max-w-full min-w-0 py-12 sm:py-16 px-4 sm:px-6 lg:px-8 border-t border-border/30" aria-labelledby="sun-lifecycle-heading">
      <div className="max-w-[76rem] mx-auto min-w-0">
        <SectionHeading eyebrow="Stellar Astrophysics & Earth's Horizon" title="The Sun's Long Goodbye" description="Trace the nuclear arc of our G-type star from zero-age main sequence through the red giant branch to a dormant white dwarf. Stellar physics dictates when Earth's biosphere dissolves." />

        {/* Interactive Workspace: Sun Model + Earth Biosphere */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch mt-8 min-w-0">
          {/* Visual Canvas Panel (Left 7 Cols) */}
          <div className="lg:col-span-7 ql-glass p-5 sm:p-7 flex flex-col justify-between gap-6 min-w-0">
            {/* Header Telemetry */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-border/30">
              <div className="flex items-center gap-2">
                <Sun className="size-4 text-primary shrink-0" aria-hidden="true" />
                <span className="font-mono text-xs uppercase tracking-widest text-primary font-semibold">
                  Solar Evolution Telemetry
                </span>
              </div>
              <div className="flex items-center gap-2">
                <EvidenceBadge level={currentStage.level} compact />
                <span className="font-mono text-xs text-muted-foreground">
                  Stellar Model [1]
                </span>
              </div>
            </div>

            {/* Live SVG Stellar Chamber */}
            <div className="relative w-full h-64 sm:h-72 rounded-xl bg-card/60 border border-border/50 flex items-center justify-center overflow-hidden p-4">
              {/* Dynamic star visual */}
              <svg viewBox="0 0 240 240" className="w-full h-full max-w-xs transition-all duration-300" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  {/* Stage-dependent radial gradients */}
                  <radialGradient id="sunGlowGrad" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor={currentStage.radiusSolar > 50 ? "hsl(0 84% 60%)" : currentStage.radiusSolar < 0.1 ? "hsl(186 100% 70%)" : "hsl(38 95% 65%)"} stopOpacity="1" />
                    <stop offset="60%" stopColor={currentStage.radiusSolar > 50 ? "hsl(0 84% 45%)" : currentStage.radiusSolar < 0.1 ? "hsl(215 100% 60%)" : "hsl(38 95% 50%)"} stopOpacity="0.8" />
                    <stop offset="100%" stopColor="transparent" stopOpacity="0" />
                  </radialGradient>

                  {/* Planetary nebula shell for white dwarf stage */}
                  <radialGradient id="pnNebulaGrad" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="transparent" />
                    <stop offset="70%" stopColor="hsl(186 100% 50% / 0.35)" />
                    <stop offset="90%" stopColor="hsl(270 70% 55% / 0.45)" />
                    <stop offset="100%" stopColor="transparent" />
                  </radialGradient>
                </defs>

                {/* If planetary nebula stage, render expanding gaseous envelope */}
                {currentStage.radiusSolar < 0.1 && <circle cx="120" cy="120" r="105" fill="url(#pnNebulaGrad)" stroke="hsl(186 100% 50% / 0.5)" strokeWidth="1.5" strokeDasharray="4 6" />}

                {/* Outer Photospheric Glow */}
                <circle cx="120" cy="120" r={renderRadius + 18} fill="url(#sunGlowGrad)" opacity="0.6" />

                {/* Sun Core Disk */}
                <circle cx="120" cy="120" r={renderRadius} fill={currentStage.radiusSolar > 50 ? "hsl(0 84% 55%)" : currentStage.radiusSolar < 0.1 ? "hsl(186 100% 90%)" : "hsl(38 95% 60%)"} stroke="hsl(0 0% 100% / 0.3)" strokeWidth="2" className="transition-all duration-300" />

                {/* Hot central specular core */}
                <circle cx="120" cy="120" r={Math.max(4, renderRadius * 0.3)} fill="hsl(0 0% 100% / 0.85)" />

                {/* 1 AU orbital guideline for perspective */}
                <circle cx="120" cy="120" r="85" stroke="hsl(186 100% 50% / 0.25)" strokeWidth="1" strokeDasharray="2 4" />
                <text x="120" y="28" textAnchor="middle" fill="hsl(240 8% 68%)" fontSize="8" fontFamily="var(--font-mono)">
                  Earth's Orbit (~1 AU)
                </text>
              </svg>

              {/* Watermark badge inside canvas */}
              <div className="absolute bottom-3 left-3 bg-background/80 backdrop-blur-md px-2.5 py-1 rounded border border-border/40 font-mono text-xs text-foreground">
                <span className="text-primary font-bold">{currentStage.stageName}</span>
                <span className="text-muted-foreground ml-2">
                  (Age: {currentStage.ageGyr.toFixed(1)} Gyr)
                </span>
              </div>
            </div>

            {/* Slider Control */}
            <div className="flex flex-col gap-2 pt-2">
              <div className="flex items-center justify-between text-xs font-mono text-muted-foreground">
                <span>Age: 0.0 Gyr (Birth)</span>
                <span className="text-primary font-bold">
                  Selected: {currentStage.ageGyr.toFixed(1)} Billion Years
                </span>
                <span>12.0 Gyr (White Dwarf)</span>
              </div>

              <input type="range" min={0} max={SUN_LIFECYCLE_STAGES.length - 1} step={1} value={sliderIndex} onChange={e => setSliderIndex(Number(e.target.value))} className="ob-timeline-slider w-full" aria-label="Solar evolution age slider (0 to 12 billion years)" aria-valuetext={`${currentStage.label} at ${currentStage.ageGyr} billion years`} />

              <div className="flex justify-between text-[11px] font-mono text-muted-foreground mt-1 overflow-x-auto gap-2">
                {SUN_LIFECYCLE_STAGES.map((s, idx) => <button key={s.ageGyr} type="button" onClick={() => setSliderIndex(idx)} className={`hover:text-primary transition-colors text-left shrink-0 ${idx === sliderIndex ? "text-primary font-bold underline" : ""}`}>
                    {s.ageGyr} Gyr
                  </button>)}
              </div>
            </div>

            {/* Key Astrophysical Metrics */}
            <div className="grid grid-cols-3 gap-2 sm:gap-4 pt-3 border-t border-border/30 text-center font-mono">
              <div className="p-2 sm:p-2.5 rounded-lg bg-card/40 border border-border/40">
                <span className="text-[10px] sm:text-xs text-muted-foreground uppercase block">
                  Radius
                </span>
                <span className="text-xs sm:text-sm font-bold text-foreground">
                  {currentStage.radiusSolar >= 10 ? `~${currentStage.radiusSolar} R☉` : `${currentStage.radiusSolar} R☉`}
                </span>
              </div>
              <div className="p-2 sm:p-2.5 rounded-lg bg-card/40 border border-border/40">
                <span className="text-[10px] sm:text-xs text-muted-foreground uppercase block">
                  Luminosity
                </span>
                <span className="text-xs sm:text-sm font-bold text-primary">
                  {currentStage.luminositySolar >= 10 ? `~${currentStage.luminositySolar} L☉` : `${currentStage.luminositySolar} L☉`}
                </span>
              </div>
              <div className="p-2 sm:p-2.5 rounded-lg bg-card/40 border border-border/40">
                <span className="text-[10px] sm:text-xs text-muted-foreground uppercase block">
                  Temp
                </span>
                <span className="text-xs sm:text-sm font-bold text-foreground">
                  {currentStage.tempK.toLocaleString()} K
                </span>
              </div>
            </div>
          </div>

          {/* Earth Habitability Panel (Right 5 Cols) */}
          <div className="lg:col-span-5 ql-glass-strong p-5 sm:p-7 flex flex-col justify-between gap-5 rounded-2xl border border-primary/30 min-w-0">
            <div>
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <Globe className="size-4 text-primary shrink-0" aria-hidden="true" />
                  <span className="font-mono text-xs uppercase tracking-widest text-primary font-semibold">
                    Earth Biosphere Impact
                  </span>
                </div>
                <EvidenceBadge level="research" compact />
              </div>

              <h3 className="font-serif text-xl sm:text-2xl font-bold text-foreground mb-1 break-words">
                Status: {currentStage.earthHabitability}
              </h3>
              <p className="font-mono text-xs text-muted-foreground mb-4">
                Epistemic classification:{" "}
                <span className="text-primary font-semibold">
                  {currentStage.level === "established" ? "Established stellar physics" : "Active climate & orbital modeling"}
                </span>
                <Footnote n={1} />
              </p>

              <div className="p-4 rounded-xl bg-card/50 border border-border/60 text-sm text-foreground leading-relaxed font-sans break-words mb-5">
                {currentStage.earthNotes}
              </div>

              {/* Core scientific facts regarding the Sun and Earth */}
              <div className="flex flex-col gap-3 font-sans text-xs text-muted-foreground">
                <div className="flex items-start gap-2.5">
                  <Flame className="size-4 text-amber-400 shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-foreground">Luminosity Rate:</strong> The Sun
                    brightens by roughly 10% every billion years as helium accumulates in
                    the core.
                  </div>
                </div>

                <div className="flex items-start gap-2.5">
                  <AlertTriangle className="size-4 text-rose-400 shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-foreground">Ocean Dissociation:</strong> In
                    roughly 1 to 1.5 billion years, atmospheric temperatures trigger a moist
                    greenhouse state, permanently evaporating Earth's liquid oceans.
                  </div>
                </div>

                <div className="flex items-start gap-2.5">
                  <Snowflake className="size-4 text-cyan-400 shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-foreground">Engulfment Probability:</strong>{" "}
                    Schröder & Smith (2008) calculate that while mass loss widens Earth's
                    orbit to ~1.15 AU, chromospheric tidal drag makes engulfment inside the
                    red giant probable.
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Caution Footnote */}
            <div className="pt-3 border-t border-border/40 text-[11px] font-mono text-muted-foreground flex items-center justify-between">
              <span>Model Reference: Schröder & Smith 2008</span>
              <span className="text-primary">MNRAS 386:155</span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}